import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Chip,
  Fade,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Link, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import { whatsAppHref } from '../../constants/conversion';
import { mailtoSales, SALES_EMAIL } from '../../constants/contact';
import WhatsAppBrandIcon from '../WhatsAppBrandIcon';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Fetch header data from API
  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        const response = await apiService.getHeader();
        if (response && response.success && response.header) {
          setHeaderData(response.header);
        }
      } catch (error) {
        console.error('Error fetching header data:', error);
        // Use default values if API fails
        setHeaderData({
          logo_url: '/assets/logos/bslogo.png',
          company_name: 'Boldcomp Solutions',
          phone_number: '0726 642 205',
          phone_label: 'Call us now',
          menu_items: [
            { text: 'Services', path: '/services' },
            { text: 'Courses', path: '/courses' },
            { text: 'AI Solutions', path: '/ai-solutions' },
            { text: 'About', path: '/about' },
            { text: 'Blogs', path: '/blogs' },
            { text: 'Careers', path: '/careers' },
            { text: 'Contact', path: '/contact' },
          ],
          cta_text: 'Get a free project scope',
          cta_link: '/request-quote',
        });
      }
    };
    fetchHeaderData();
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const defaultItems = [
    { text: 'Services', path: '/services' },
    { text: 'Portfolio', path: '/portfolio' },
    { text: 'Courses', path: '/courses' },
    { text: 'AI Solutions', path: '/ai-solutions' },
    { text: 'About', path: '/about' },
    { text: 'Blogs', path: '/blogs' },
    { text: 'Careers', path: '/careers' },
    { text: 'Contact', path: '/contact' },
  ];
  let rawItems = headerData?.menu_items;
  if (typeof rawItems === 'string') {
    try {
      rawItems = JSON.parse(rawItems);
    } catch {
      rawItems = defaultItems;
    }
  }
  let menuItems = Array.isArray(rawItems) && rawItems.length > 0 ? rawItems : defaultItems;
  /* Never show "New" chip on AI Solutions (CMS JSON may still include it) */
  menuItems = menuItems.map((item) =>
    item?.path === '/ai-solutions' ? { ...item, badge: undefined } : item
  );
  
  const desktopNavItems = menuItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      sx={{
        textAlign: 'center',
        p: 2,
        height: '100%',
        background: 'linear-gradient(180deg, #16184a 0%, #1D1D4E 100%)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box component={Link} to="/" sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }} onClick={handleDrawerToggle}>
          <Box
            component="img"
            src={headerData?.logo_url || '/assets/logos/bslogo.png'}
            alt=""
            sx={{
              height: 40,
              width: 'auto',
              filter: 'brightness(0) invert(1)',
            }}
          />
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List sx={{ mt: 2 }}>
        <ListItem sx={{ justifyContent: 'center', py: 1.5, flexDirection: 'column', gap: 0.5 }}>
          <Button
            component="a"
            href={mailtoSales('Website enquiry')}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.95)', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
          >
            {SALES_EMAIL}
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
            {headerData?.phone_number || '0726 642 205'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            {headerData?.phone_label || 'Call us'}
          </Typography>
        </ListItem>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              color: 'white',
              fontWeight: location.pathname === item.path ? 700 : 400,
              textDecoration: 'none',
              justifyContent: 'center',
              borderRadius: 2,
              mb: 1,
              backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.text}
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor: 'rgba(31, 182, 255, 0.25)',
                        color: '#a8e8ff',
                        fontSize: '0.65rem',
                        height: 20,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              } 
            />
          </ListItem>
        ))}
        <ListItem sx={{ justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to={headerData?.cta_link || '/request-quote'}
            onClick={handleDrawerToggle}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.25,
              fontWeight: 700,
              color: 'primary.main',
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 4px 14px rgba(14, 139, 199, 0.35)' },
            }}
          >
            {headerData?.cta_text || 'Get a free project scope'}
          </Button>
        </ListItem>
        
        {/* Contact Info in Mobile Drawer */}
        <ListItem sx={{ justifyContent: 'center', mt: 3, flexDirection: 'column', gap: 1 }}>
          <Button
            component={Link}
            to="/portfolio"
            onClick={handleDrawerToggle}
            sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'none', fontWeight: 600 }}
          >
            Client work &amp; portfolio
          </Button>
        </ListItem>
        <ListItem sx={{ justifyContent: 'center', mt: 2 }}>
          <IconButton
            component="a"
            href={whatsAppHref('Hi Boldcomp — I found you on the website.')}
            target="_blank"
            rel="noopener noreferrer"
            className="wa-pulse-ring"
            sx={{
              bgcolor: '#25D366',
              color: 'white',
              '&:hover': { bgcolor: '#20bd5a' },
            }}
            aria-label="WhatsApp"
          >
            <WhatsAppBrandIcon sx={{ fontSize: 26 }} />
          </IconButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.97)' : 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: scrolled ? '0 1px 0 rgba(15, 23, 42, 0.08), 0 8px 24px rgba(15, 23, 42, 0.06)' : '0 1px 0 rgba(15, 23, 42, 0.06)',
        color: theme.palette.primary.main,
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Desktop: email & phone row above menu */}
        {!isMobile && (
          <Fade in timeout={400}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pt: 1,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'rgba(15, 23, 42, 0.08)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: { md: 2.5, lg: 3.5 },
                  rowGap: 1,
                }}
              >
                <Box
                  component="a"
                  href={mailtoSales('Website enquiry')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                  }}
                >
                  <EmailOutlinedIcon sx={{ fontSize: 20, opacity: 0.85 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.25 }}>
                    {SALES_EMAIL}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(14, 139, 199, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main,
                      border: '1px solid rgba(14, 139, 199, 0.12)',
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1.2, fontSize: '0.8125rem' }}>
                      {headerData?.phone_number || '0726 642 205'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem', display: 'block' }}>
                      {headerData?.phone_label || 'Call us'}
                    </Typography>
                  </Box>
                </Box>

                <Tooltip title="WhatsApp">
                  <IconButton
                    component="a"
                    href={whatsAppHref('Hi Boldcomp — I found you on the website.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wa-pulse-ring wa-soft-blink"
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: '#25D366',
                      color: 'white',
                      '&:hover': { bgcolor: '#20bd5a' },
                    }}
                    aria-label="WhatsApp"
                  >
                    <WhatsAppBrandIcon sx={{ fontSize: 26 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Fade>
        )}

        {/* Mobile: one row */}
        <Toolbar
          disableGutters
          sx={{
            display: { xs: 'flex', md: 'flex' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            py: { xs: scrolled ? 0.75 : 1, md: scrolled ? 0.85 : 1.1 },
            minHeight: { xs: 56, md: 58 },
            transition: 'all 0.3s ease',
          }}
        >
          <Box component={Link} to="/" sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Fade in timeout={800}>
              <Box
                component="img"
                src={headerData?.logo_url || '/assets/logos/bslogo.png'}
                alt=""
                sx={{
                  height: { xs: scrolled ? 34 : 38, md: scrolled ? 36 : 40 },
                  width: 'auto',
                  transition: 'all 0.3s ease',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Fade>
          </Box>

          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                gap: { md: 0.35, lg: 0.5 },
                flexWrap: 'wrap',
                px: { md: 0.5, lg: 1 },
              }}
            >
              {desktopNavItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: location.pathname === item.path ? theme.palette.secondary.main : theme.palette.primary.main,
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    fontSize: '0.875rem',
                    px: 1.5,
                    py: 0.65,
                    borderRadius: 1.5,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: location.pathname === item.path ? '70%' : '0%',
                      height: '2px',
                      backgroundColor: theme.palette.secondary.main,
                      transition: 'width 0.2s ease',
                      transform: 'translateX(-50%)',
                    },
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      backgroundColor: 'rgba(14, 139, 199, 0.08)',
                      '&:before': { width: '70%' },
                    },
                    transition: 'color 0.15s ease, background-color 0.15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.text}
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          ml: 0.75,
                          backgroundColor: 'rgba(14, 139, 199, 0.12)',
                          color: 'secondary.dark',
                          fontSize: '0.65rem',
                          height: 20,
                          fontWeight: 700,
                          border: '1px solid rgba(14, 139, 199, 0.25)',
                        }}
                      />
                    )}
                  </Box>
                </Button>
              ))}
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                to={headerData?.cta_link || '/request-quote'}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 0.65,
                  ml: { md: 0.5, lg: 1 },
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'primary.main',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { boxShadow: '0 4px 16px rgba(14, 139, 199, 0.35)' },
                }}
              >
                {headerData?.cta_text || 'Free project scope'}
              </Button>
            </Box>
          )}

          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="WhatsApp">
                <IconButton
                  component="a"
                  href={whatsAppHref('Hi Boldcomp — I found you on the website.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wa-pulse-ring wa-soft-blink"
                  size="small"
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    '&:hover': { bgcolor: '#20bd5a' },
                  }}
                  aria-label="WhatsApp"
                >
                  <WhatsAppBrandIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(14, 139, 199, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(14, 139, 199, 0.15)' },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ 
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
            }
          }
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            border: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;